import { fromEvent, merge, tap } from 'rxjs';
import {
  RegisterExtensionEditorSetup,
  RegisterExtensionRender,
} from '../../types/registerExtension';
import { Editor, EditorOptions } from 'tinymce';
import {
  checkmarkCircle,
  closeCircle,
  dice,
} from 'ionicons/icons';
import { ModalController } from '@ionic/angular/standalone';
import { SelectOperationComponent } from 'src/app/features/operations/components/select-operation/select-operation.component';
import { createRollModalOptions } from 'src/app/features/operations/utils/roll-modal.util';
import { SelectImpersonateService } from 'src/app/features/chat/services/select-impersonate.service';
import { NamedOperation, parseModifiers } from 'operationlib';
import { OperationDetailComponent } from 'src/app/features/operations/components/operation-detail/operation-detail.component';
import { StoryService } from 'src/app/features/stories/story.service';
import { buildOperationChatSpan } from './operation-chat-span';

function ioniconDataUrlToSvg(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

/** Filled icons: plain SVG in innerHTML (ion-icon is not upgraded there). */
const OP_BOOL_OK_SVG = ioniconDataUrlToSvg(checkmarkCircle);
const OP_BOOL_BAD_SVG = ioniconDataUrlToSvg(closeCircle);

/** Modifiers live after the first `;` (same convention as send-message / operationlib). */
function modifiersSegment(attr: string | null | undefined): string {
  if (!attr) return '';
  const i = attr.indexOf(';');
  return i === -1 ? '' : attr.slice(i + 1);
}

/** Detect `r:boolean` even if spacing differs or parseModifiers misses an edge case. */
function modifierSegmentDeclaresBoolean(segment: string): boolean {
  const s = segment.trim();
  if (!s) return false;
  if (/\br\s*:\s*boolean\b/i.test(s)) return true;
  return parseModifiers(s).some(
    (m) => m.type === 'result' && m.value === 'boolean'
  );
}

function operationHasBooleanResultModifier(
  dataOperation: string | null | undefined,
  statCompiled: string | null | undefined,
  dataFinal: string | null | undefined,
  dataFinalRolled: string | null | undefined
): boolean {
  for (const raw of [
    modifiersSegment(dataOperation),
    modifiersSegment(statCompiled),
    modifiersSegment(dataFinal),
    modifiersSegment(dataFinalRolled),
  ]) {
    if (modifierSegmentDeclaresBoolean(raw)) return true;
  }
  return false;
}

function buildOperationResultInnerHtml(
  resultAttr: string | null,
  dataOperation: string | null,
  statCompiled: string | null,
  dataFinal: string | null,
  dataFinalRolled: string | null
): string {
  const resultVal = resultAttr;
  if (!resultVal) return '';
  const isBool = operationHasBooleanResultModifier(
    dataOperation,
    statCompiled,
    dataFinal,
    dataFinalRolled
  );
  if (isBool) {
    /* Allineato a operationlib README: boolean “vero” se valore > 0 (es. 1/0 da ternary). */
    const isSuccess = +resultVal > 0;
    return isSuccess
      ? `<span class="op-chip__result op-chip__result--success" role="img" aria-label="Successo">${OP_BOOL_OK_SVG}</span>`
      : `<span class="op-chip__result op-chip__result--fail" role="img" aria-label="Fallimento">${OP_BOOL_BAD_SVG}</span>`;
  }
  return `<span class="op-chip__result">${resultVal}</span>`;
}

const operationExtensionRender: RegisterExtensionRender = (
  editor,
  injector
) => {
  // const router = injector.get(Router);
  const modalCtrl = injector.get(ModalController);
  return merge(
    ...Array.from(editor.querySelectorAll('[data-operation-result]')).map(
      (el) => {
        const host = el as HTMLElement;
        const inner = buildOperationResultInnerHtml(
          host.getAttribute('data-operation-result'),
          host.getAttribute('data-operation'),
          host.getAttribute('data-operation-stat-compiled'),
          host.getAttribute('data-operation-final'),
          host.getAttribute('data-operation-final-rolled')
        );
        const resultEl = host.querySelector('.result');
        if (inner) {
          if (resultEl) {
            resultEl.innerHTML = inner;
          } else {
            /* Legacy HTML: TinyMCE sometimes stripped empty <strong class="result"> */
            host.insertAdjacentHTML(
              'beforeend',
              `<strong class="result">${inner}</strong>`
            );
          }
        } else if (resultEl) {
          resultEl.innerHTML = '';
        }
        return fromEvent(host, 'click').pipe(
          tap(() => {
            modalCtrl
              .create({
                component: OperationDetailComponent,
                componentProps: {
                  name: host.getAttribute('data-operation-name'),
                  character: host.getAttribute('data-character') ?? undefined,
                  original: host.getAttribute('data-operation'),
                  statSelected: host.getAttribute('data-operation-stat-selected'),
                  statCompiled: host.getAttribute('data-operation-stat-compiled'),
                  rolled: host.getAttribute('data-operation-final-rolled'),
                  final: host.getAttribute('data-operation-final'),
                  result: host.getAttribute('data-operation-result'),
                },
                showBackdrop: true,
                initialBreakpoint: 0.25,
                breakpoints: [0, 0.25, 1],
              })
              .then((m) => m.present());
          })
        );
      }
    )
  );
};

const operationExtensionEditorSetup: RegisterExtensionEditorSetup = (
  editor: Editor,
  injector
) => {
  const story = injector.get(StoryService, null);
  let modalCtrl = injector.get(ModalController);
  let impersonate = injector.get(SelectImpersonateService, null);
  editor.ui.registry.addIcon('dice', dice.slice(dice.indexOf(',') + 1));
  editor.ui.registry.addButton('operation', {
    icon: 'dice',
    onAction: (_) => {
      modalCtrl
        .create(
          createRollModalOptions(SelectOperationComponent, {
            impersonateChar: impersonate?.impersonate(),
            storyOperations: story?.data()?.operations,
          }),
        )
        .then((modal) => {
          modal.onWillDismiss<NamedOperation>().then(async (res) => {
            if (res.data) {
              editor.insertContent(
                buildOperationChatSpan(
                  res.data,
                  impersonate?.impersonate()?.id ?? '',
                  impersonate?.impersonate()?.name
                )
              );
            }
          });
          modal.present();
        });
    },
  });
};
const operationExtensionEditorFormat: EditorOptions['formats'] = {};

export const operationExtension = {
  render: operationExtensionRender,
  setup: operationExtensionEditorSetup,
  format: operationExtensionEditorFormat,
};
